import type { ColumnsType, AnyObject } from "@similarweb/pro-logic-components";
import { ChangeCell } from "@similarweb/pro-logic-components";

import { TrafficShareWithVisits, TrendCell, VisitsThreshHoldCell } from "app/components/React/Table/cells";
import { LOBTooltip } from "app/components/React/Tooltip/LOBTooltip/LOBTooltip";
import { NO_DATA_SIGN } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { DEFAULT_DECIMAL, FORMATTERS, PERCENTAGE } from "app/common/benchmark/formatters";

import { PERCENTAGE_SHARE_THRESHOLD, VISITS_DATA_THRESHOLD } from "../../consts";
import { getYoYChangeTooltip } from "../helpers";
import LobGroupTrafficShare from "../shared/LobGroupTrafficShare/LobGroupTrafficShare";

export type SortConfig = {
    key: string;
    ascending: boolean;
};

const getSortOrder = (sort: SortConfig, columnKey: string): "ascend" | "descend" | undefined => {
    return sort.key === columnKey ? (sort.ascending ? "ascend" : "descend") : undefined;
};

const renderYoYChangeCell = (change: number) => {
    const formattedChange = change && `${FORMATTERS.percent(change, DEFAULT_DECIMAL)}${PERCENTAGE}`;
    return (
        <ChangeCell
            formattedValue={formattedChange}
            value={change}
        />
    );
};

const renderCompareModeTotalVisitsCell = (totalVisits: { visits: number }) => {
    if (!totalVisits.visits) {
        return NO_DATA_SIGN;
    }
    return <VisitsThreshHoldCell value={totalVisits.visits} />;
};

const renderSingleModeTotalVisitsCell = (totalVisits: { share: string; visits: number }) => {
    if (!totalVisits) {
        return NO_DATA_SIGN;
    }
    const { share, visits } = totalVisits;
    return (
        <TrafficShareWithVisits
            row={{ TotalVisits: visits }}
            value={share}
            applyAbbrNumberFilter
            undefinedSign={NO_DATA_SIGN}
            minPercentageToShow={PERCENTAGE_SHARE_THRESHOLD}
            thresholdTrafficShareWithVisitsPercentage={PERCENTAGE_SHARE_THRESHOLD}
            thresholdForTotalFilter={VISITS_DATA_THRESHOLD}
        />
    );
};

const renderTrafficShareCell = (value: Record<string, number>, record: AnyObject) => {
    if (!value || Object.keys(value).length === 0) {
        return NO_DATA_SIGN;
    }
    return (
        <LobGroupTrafficShare
            trafficShare={record.trafficShare}
            totalVisits={record.totalVisits}
            domainYoYChanges={record.domainYoyChanges}
        />
    );
};

const renderYearlyTrendCell = (value: any) => (
    <TrendCell
        value={value}
        row={{ field: "visitsTrend" }}
    />
);

const getLobNameColumn = (rowsLength: number, sort: SortConfig) => ({
    title: translate("analysis.content.lob.table.column.line.of.business", { numberOfLobs: rowsLength }),
    dataIndex: "lobName",
    key: "lobName",
    width: 340,
    sorter: (a: AnyObject, b: AnyObject) => a.lobName.localeCompare(b.lobName),
    sortDirections: ["descend", "ascend"] as ("descend" | "ascend")[],
    sortOrder: getSortOrder(sort, "lobName"),
    render: (lobName: string) => {
        return (
            <LOBTooltip
                domain={lobName}
                placement="top-left"
            >
                <div>{lobName}</div>
            </LOBTooltip>
        );
    },
    tooltipTitle: translate("analysis.content.lob.table.column.line.of.business.tooltip"),
});

const getYoYChangeColumn = (sort: SortConfig) => ({
    title: translate("global.change.yoy"),
    dataIndex: "yoyChange",
    width: 114,
    align: "right" as const,
    sorter: (a: AnyObject, b: AnyObject) => a.yoyChange - b.yoyChange,
    sortDirections: ["descend", "ascend"] as ("descend" | "ascend")[],
    sortOrder: getSortOrder(sort, "yoyChange"),
    render: renderYoYChangeCell,
    tooltipTitle: getYoYChangeTooltip(),
});

const getCompareModeTotalVisitsColumn = (sort: SortConfig) => ({
    title: translate("analysis.content.lob.table.column.total.visits"),
    dataIndex: "totalVisits",
    key: "totalVisits",
    width: 107,
    align: "right" as const,
    sorter: (a: AnyObject, b: AnyObject) => (a.totalVisits?.visits ?? 0) - (b.totalVisits?.visits ?? 0),
    sortDirections: ["descend", "ascend"] as ("descend" | "ascend")[],
    sortOrder: getSortOrder(sort, "totalVisits"),
    render: renderCompareModeTotalVisitsCell,
    tooltipTitle: translate("analysis.content.lob.table.column.total.visits.tooltip"),
});

const getCompareModeTrafficShareColumn = () => ({
    title: translate("analysis.content.lob.table.column.traffic.share"),
    dataIndex: "trafficShare",
    width: 340,
    align: "left" as const,
    render: renderTrafficShareCell,
});

const getSingleModeTotalVisitsColumn = (sort: SortConfig) => ({
    title: translate("analysis.content.lob.table.column.total.visits"),
    dataIndex: "totalVisits",
    key: "totalVisits",
    width: 400,
    sorter: (a: AnyObject, b: AnyObject) => (a.totalVisits?.visits ?? 0) - (b.totalVisits?.visits ?? 0),
    sortDirections: ["descend", "ascend"] as ("descend" | "ascend")[],
    sortOrder: getSortOrder(sort, "totalVisits"),
    render: renderSingleModeTotalVisitsCell,
    tooltipTitle: translate("analysis.content.lob.table.column.total.visits.tooltip"),
});

const getSingleModeYearlyTrendColumn = () => ({
    title: translate("analysis.content.lob.table.column.yearly.trend"),
    dataIndex: "visitsTrend",
    width: 100,
    align: "left" as const,
    render: renderYearlyTrendCell,
    tooltipTitle: translate("analysis.content.lob.table.column.yearly.trend.tooltip"),
});

export const getLobTableColumns = (
    rowsLength: number,
    sort: SortConfig,
    isCompareMode: boolean,
    filteredLobsData: Array<{ Lob: string; lobKey: string }> = [],
): ColumnsType<AnyObject> => {
    const baseColumn = getLobNameColumn(rowsLength, sort);
    const yoyChangeColumn = getYoYChangeColumn(sort);

    if (isCompareMode) {
        const compareTotalVisitsColumn = getCompareModeTotalVisitsColumn(sort);
        const compareTrafficShareColumn = getCompareModeTrafficShareColumn();
        return [baseColumn, compareTotalVisitsColumn, yoyChangeColumn, compareTrafficShareColumn];
    } else {
        const singleTotalVisitsColumn = getSingleModeTotalVisitsColumn(sort);
        const singleYearlyTrendColumn = getSingleModeYearlyTrendColumn();
        return [baseColumn, singleTotalVisitsColumn, yoyChangeColumn, singleYearlyTrendColumn];
    }
};
